/**
 * Section theme tokens for the standardized PageHero / PageHeroCompact
 * / PageShell components. Each section has a hero gradient, a
 * matching subtle body tint, and the text-on-gradient palette.
 *
 * To use:
 *   import { getSectionTheme } from '../lib/sectionThemes';
 *   const theme = getSectionTheme('LEI');
 *   <PageHero theme={theme} title="…" />
 *
 * Adding a new section: pick a Tailwind family that matches the
 * existing token vocabulary (no new bespoke palettes).
 */

export const SECTION_THEMES = {
  Maturity: {
    name: 'Maturity',
    heroGradient: 'bg-gradient-to-br from-teal-600 to-teal-700',
    textLight: 'text-teal-50',
    textLighter: 'text-teal-100',
    bodyTint: 'bg-teal-50/30',
    actionBtn: 'bg-white text-teal-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'teal',
    accentBorder: 'border-teal-500',
    accentText: 'text-teal-700',
  },
  LEI: {
    name: 'LEI',
    // Slate to match the ROI Waterfall "LEI" node (Learning
    // Improvement column).
    heroGradient: 'bg-gradient-to-br from-slate-700 to-slate-900',
    textLight: 'text-slate-50',
    textLighter: 'text-slate-200',
    bodyTint: 'bg-slate-50/40',
    actionBtn: 'bg-white text-slate-800 hover:bg-white/90',
    statBg: 'bg-white/10 hover:bg-white/15',
    accent: 'slate',
    accentBorder: 'border-slate-600',
    accentText: 'text-slate-700',
  },
  PE: {
    name: 'PE',
    // Purple to match the ROI Waterfall "PE Ideal" node
    // (Predicted Capacity column).
    heroGradient: 'bg-gradient-to-br from-purple-600 to-purple-700',
    textLight: 'text-purple-50',
    textLighter: 'text-purple-100',
    bodyTint: 'bg-purple-50/30',
    actionBtn: 'bg-white text-purple-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'purple',
    accentBorder: 'border-purple-500',
    accentText: 'text-purple-700',
  },
  ROE: {
    name: 'ROE',
    // Teal to match the ROI Waterfall "ROE" node (Return on
    // Expectation column).
    heroGradient: 'bg-gradient-to-br from-teal-600 to-teal-700',
    textLight: 'text-teal-50',
    textLighter: 'text-teal-100',
    bodyTint: 'bg-teal-50/30',
    actionBtn: 'bg-white text-teal-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'teal',
    accentBorder: 'border-teal-500',
    accentText: 'text-teal-700',
  },
  ROI: {
    name: 'ROI',
    // Amber/orange to match the ROI Waterfall "ROI" node
    // (Financial Return column).
    heroGradient: 'bg-gradient-to-br from-amber-500 to-amber-700',
    textLight: 'text-amber-50',
    textLighter: 'text-amber-100',
    bodyTint: 'bg-amber-50/30',
    actionBtn: 'bg-white text-amber-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'amber',
    accentBorder: 'border-amber-500',
    accentText: 'text-amber-700',
  },
  Benchmarking: {
    name: 'Benchmarking',
    heroGradient: 'bg-gradient-to-br from-purple-600 to-purple-700',
    textLight: 'text-purple-50',
    textLighter: 'text-purple-100',
    bodyTint: 'bg-purple-50/30',
    actionBtn: 'bg-white text-purple-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'purple',
    accentBorder: 'border-purple-500',
    accentText: 'text-purple-700',
  },
  ImpactModelling: {
    name: 'Impact Modelling',
    heroGradient: 'bg-gradient-to-br from-indigo-600 to-violet-700',
    textLight: 'text-indigo-50',
    textLighter: 'text-indigo-100',
    bodyTint: 'bg-indigo-50/30',
    actionBtn: 'bg-white text-indigo-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'indigo',
    accentBorder: 'border-indigo-500',
    accentText: 'text-indigo-700',
  },
  Admin: {
    name: 'Admin',
    heroGradient: 'bg-gradient-to-br from-slate-700 to-slate-900',
    textLight: 'text-slate-50',
    textLighter: 'text-slate-200',
    bodyTint: 'bg-slate-50/40',
    actionBtn: 'bg-white text-slate-800 hover:bg-white/90',
    statBg: 'bg-white/10 hover:bg-white/15',
    accent: 'slate',
    accentBorder: 'border-slate-600',
    accentText: 'text-slate-700',
  },
  Insights: {
    name: 'Insights',
    heroGradient: 'bg-gradient-to-br from-rose-500 to-rose-700',
    textLight: 'text-rose-50',
    textLighter: 'text-rose-100',
    bodyTint: 'bg-rose-50/30',
    actionBtn: 'bg-white text-rose-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'rose',
    accentBorder: 'border-rose-500',
    accentText: 'text-rose-700',
  },
  Reengage: {
    name: 'Re-engage',
    heroGradient: 'bg-gradient-to-br from-cyan-500 to-cyan-700',
    textLight: 'text-cyan-50',
    textLighter: 'text-cyan-100',
    bodyTint: 'bg-cyan-50/30',
    actionBtn: 'bg-white text-cyan-700 hover:bg-white/90',
    statBg: 'bg-white/15 hover:bg-white/20',
    accent: 'cyan',
    accentBorder: 'border-cyan-500',
    accentText: 'text-cyan-700',
  },
  Navy: {
    // Mirrors the inline navy gradient used by the Getting
    // Started hero on the Dashboard. Reach for this when a
    // "neutral, executive" look is wanted instead of one of the
    // section-coloured palettes (used by Solution Finder etc.).
    name: 'Navy',
    heroGradient: 'bg-gradient-to-br from-navy-700 to-navy-800',
    textLight: 'text-blue-50',
    textLighter: 'text-blue-100',
    bodyTint: 'bg-slate-50/40',
    actionBtn: 'bg-white text-navy-700 hover:bg-white/90',
    statBg: 'bg-white/10 hover:bg-white/15',
    accent: 'navy',
    accentBorder: 'border-navy-700',
    accentText: 'text-navy-700',
  },
};

export function getSectionTheme(name) {
  return SECTION_THEMES[name] || SECTION_THEMES.ImpactModelling;
}

// ─────────────────────────────────────────────────────────────
// Light hero variant — shared by pages adopting the "Option A"
// light treatment (LIE home, Health pages). Returns the section's
// base tokens with the dark gradient replaced by a slate-50 canvas,
// title in the Seertech brand cyan + extrabold weight, and slate
// text tokens for the body/description. Other tokens (accent,
// accentBorder, accentText) are inherited from the section so
// downstream tiles can still tint themselves correctly.
//
// `titleClass` uses a literal Tailwind arbitrary value
// `text-[#44b6d2]` (not a template literal) so the JIT compiler
// picks it up reliably at build time.
// ─────────────────────────────────────────────────────────────

export function getLightSectionTheme(name) {
  const base = getSectionTheme(name);
  return {
    ...base,
    heroGradient: 'bg-slate-50',
    bodyTint: 'bg-slate-50',
    textBase: 'text-slate-900',
    textLight: 'text-slate-800',
    textLighter: 'text-slate-600',
    titleClass: 'text-[#44b6d2] font-extrabold',
    actionBtn: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50',
    statBg: 'bg-white border border-slate-200 hover:bg-slate-50',
  };
}
